import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './NewsItem.css';
import { get_news, get_news_illustration, get_news_document, get_user_info } from '../../api/functions';
import { useApi } from '../../api/api';
import { useAuth } from '../../provider/auth';
import { DocumentViewer, LoadingSpinner } from '../../components';
import { ErrorScreen } from '../';

const NewsItem = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const getUserInfoApi = useApi(get_user_info);
  const getNewsApi = useApi(get_news, 300);
  const getNewsImage = useApi(get_news_illustration);
  const getNewsDocument = useApi(get_news_document);
  const [imageUrl, setImageUrl] = useState(null);
  const [loadingImage, setLoadingImage] = useState(true);
  const [docUrl, setDocUrl] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(true);
  const [isPdf, setIsPdf] = useState(false);
  const [newsItem, setNewsItem] = useState({});
  const [date, setDate] = useState(null);
  const [newsDate, setNewsDate] = useState(null);
  const [title, setTitle] = useState(null);
  const [content, setContent] = useState(null);
  const [membersOnly, setMembersOnly] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(false);
  const [userLoaded, setUserLoaded] = useState(false);

  useEffect(() => {
    const loadUser = async () => {
      await getUserInfoApi.execute();
      setUserLoaded(true);
    };
    loadUser();
  }, [token]);

  useEffect(() => {
    if (!userLoaded) return;

    const langs = {
      'en': 'en-GB',
      'de': 'de-DE',
      'cs': 'cs-CZ'
    };
    const locale = langs[i18n.language] || 'cs-CZ';
    const loc = i18n.language === 'cs' ? 'cz' : i18n.language;
    const fetchData = async () => {
      setIsLoading(true);
      setAuthError(false);

      try {
        const data = await getNewsApi.execute({ news_id: id });
        if (data) {
          setNewsItem(data);
          const newsDate = new Date(data.created_at);
          setDate(newsDate);
          setNewsDate(newsDate.toLocaleDateString(locale));
          setTitle(data[`title_${loc}`] || data.title_cz || data.title_en || data.title_de || 'Untitled');
          setContent(data[`text_${loc}`] || data.text_cz || data.text_en || data.text_de || '');
          setMembersOnly(data.private);
        }
      } catch (error) {
        if (error.message?.includes('401') || error.message?.includes('403')) {
          setAuthError(true);
        }
        console.error('Error loading news:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id, i18n.language, userLoaded, token]);

  useEffect(() => {
    const loadImage = async () => {
      if (newsItem.illustration_filename) {
        setLoadingImage(true);
        try {
          const imageBlob = await getNewsImage.execute({ news_id: id });
          if (imageBlob instanceof Blob && !getNewsImage.error) {
            const url = URL.createObjectURL(imageBlob);
            setImageUrl(url);
          } else {
            setImageUrl(null);
          }
        } catch (error) {
          console.error('Failed to load news image:', error);
          setImageUrl(null);
        } finally {
          setLoadingImage(false);
        }
      } else {
        setLoadingImage(false);
      }
    };

    if (newsItem.id) {
      loadImage();
    }

    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [newsItem.id, newsItem.illustration_filename, id]);

  useEffect(() => {
    const loadDocument = async () => {
      if (newsItem.document_filename) {
        setLoadingDoc(true);
        try {
          const docBlob = await getNewsDocument.execute({ news_id: id });
          if (docBlob instanceof Blob && !getNewsDocument.error) {
            const url = URL.createObjectURL(docBlob);
            setDocUrl(url);
            setIsPdf(docBlob.type === 'application/pdf');
          } else {
            setDocUrl(null);
          }
        } catch (error) {
          console.error('Failed to load news document:', error);
          setDocUrl(null);
        } finally {
          setLoadingDoc(false);
        }
      } else {
        setLoadingDoc(false);
      }
    };

    if (newsItem.id) {
      loadDocument();
    }

    return () => {
      if (docUrl) {
        URL.revokeObjectURL(docUrl);
      }
    };
  }, [newsItem.id, newsItem.document_filename, id]);

  if (!userLoaded || isLoading) {
    return (
      <div className="news-item-page">
        <div className="news-item-page-loading">
          <LoadingSpinner message={t('news.loading')} />
        </div>
      </div>
    );
  }

  if (authError) {
    return <ErrorScreen error={401} />;
  }

  if (!newsItem.id) {
    return <ErrorScreen error={404} />;
  }

  return (
    <div className="news-item-page">
      <main className="news-item-page-main">
        <div className="news-item-container">
          <Link to="/news" className="news-item-back-button">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            {t('news.backToNews')}
          </Link>

          <div className="news-item-header">
            {newsDate && <span className="news-item-date">{newsDate}</span>}
            {membersOnly && <span className="news-item-badge">{t('news.onlyForMembers')}</span>}
          </div>

          <h1 className="news-item-title">{title}</h1>

          {newsItem.illustration_filename && imageUrl && (
            <div className="news-item-media">
              <div className="news-item-single-image">
                <img src={imageUrl} alt={title} />
              </div>
            </div>
          )}

          {content && (
            <div className="news-item-text-section">
              <div className="news-item-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
              </div>
            </div>
          )}

          {newsItem.document_filename && docUrl && (
            <DocumentViewer docUrl={docUrl} isPdf={isPdf} />
          )}
        </div>
      </main>
    </div>
  );
};

export default NewsItem;
